import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { FairLaunchTheme } from "../LaunchPad/CeateFairLaunch/FairLaunchTheme";
import { getNetworkConfig } from "../ContractAction/ContractDependency";

/**
 * Reusable component to display contract address exclusion warnings
 * @param {string} contractType - Type of contract: 'lock', 'fairlaunch', 'launchpad', 'airdrop', 'multisender'
 * @param {string} customAddress - Optional: Override with custom address instead of fetching from network config
 * @param {string} customMessage - Optional: Custom warning message
 */
const ContractAddressWarning = ({
  contractType = "lock",
  customAddress = null,
  customMessage = null,
}) => {
  const [contractAddress, setContractAddress] = useState(customAddress);

  useEffect(() => {
    if (!customAddress) {
      const fetchAddress = async () => {
        const { addresses } = await getNetworkConfig();

        switch (contractType.toLowerCase()) {
          case "lock":
            setContractAddress(addresses?.LockTokenContractAddress);
            break;
          case "fairlaunch":
            setContractAddress(addresses?.FAIRLAUNCHFACTORYDDRESS);
            break;
          case "launchpad":
            setContractAddress(addresses?.LAUNCHPADCONTRACTADDRESSFACTORY);
            break;
          case "airdrop":
            setContractAddress(addresses?.AirDropFactoryContractAddress);
            break;
          case "multisender":
            setContractAddress(addresses?.MultiSenderContractAddress);
            break;
          default:
            console.warn(`Unknown contract type: ${contractType}`);
        }
      };

      fetchAddress();
    }
  }, [contractType, customAddress]);

  // Get appropriate title and message based on contract type
  const getWarningContent = () => {
    if (customMessage) {
      return {
        title: "⚠️ Important Notice",
        message: customMessage,
      };
    }

    const contractNames = {
      lock: "TrendPad's Lock",
      fairlaunch: "TrendPad's Fair Launch Factory",
      launchpad: "TrendPad's Launchpad Factory",
      airdrop: "TrendPad's AirDrop Factory",
      multisender: "TrendPad's MultiSender",
    };

    const contractName =
      contractNames[contractType.toLowerCase()] || "TrendPad";

    // Special message for lock contract (rebase tokens)
    if (contractType.toLowerCase() === "lock") {
      return {
        title: "⚠️ Important: Rebase Tokens Not Supported",
        message: (
          <>
            Please exclude {contractName} contract address{" "}
            <strong
              style={{
                color: "#2196f3",
                wordBreak: "break-all",
                fontWeight: 600,
              }}
            >
              {contractAddress}
            </strong>{" "}
            from fees, rewards, max transaction limits, and rebase mechanics
            before creating a lock. We do not support rebase tokens.
          </>
        ),
      };
    }

    // Standard message for other contracts
    return {
      title: "⚠️ Important: Exclude Contract Address",
      message: (
        <>
          Please exclude {contractName} address{" "}
          <strong
            style={{
              color: "#2196f3",
              wordBreak: "break-all",
              fontWeight: 600,
            }}
          >
            {contractAddress}
          </strong>{" "}
          from fees, rewards, and max transaction limits before creating pools.
        </>
      ),
    };
  };

  const { title, message } = getWarningContent();

  // Don't render if no address is available
  if (!contractAddress) {
    return null;
  }

  return (
    <Box
      sx={(theme) => ({
        ...FairLaunchTheme.warningBox(theme),
        mt: { xs: 3, sm: 4 },
        mb: { xs: 2, sm: 3 },
        mx: { xs: 0, sm: 0 },
      })}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#FF4444",
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 600,
          fontSize: { xs: "1rem", sm: "1.15rem" },
          mb: 1.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: (theme) => theme.palette.text.primary,
          lineHeight: 1.7,
          wordBreak: "break-word",
          fontSize: { xs: "0.9rem", sm: "1rem" },
        }}
      >
        {message}
      </Typography>
    </Box>
  );
};

export default ContractAddressWarning;
